import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const HomePage = () => {
  const { t } = useTranslation();

  const features = [
    { key: "free", icon: "💰" },
    { key: "search", icon: "🔍" },
    { key: "direct", icon: "🤝" },
    { key: "exposure", icon: "📈" },
    { key: "parent", icon: "👨‍👩‍👧‍👦" },
    { key: "safe", icon: "🛡️" },
  ];

  const steps = [1, 2, 3];

  return (
    <div className="page">
      {/* Recipe Zone Section */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left side - Food Emoji icons */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-8 md:w-1/3 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-7xl mb-4 animate-bounce">🍗</div>
                  <div className="flex justify-center gap-2 text-4xl">
                    <span>👨‍🍳</span>
                    <span>🍳</span>
                    <span>🥘</span>
                  </div>
                  <div className="flex justify-center gap-2 text-3xl mt-2">
                    <span>🥩</span>
                    <span>🥚</span>
                    <span>🧂</span>
                  </div>
                </div>
              </div>

              {/* Right side - Content */}
              <div className="p-8 md:w-2/3">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  🍗 美食食譜區
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  探索美味料理的秘密！學習經典家常菜的製作方法，讓烹飪變得簡單有趣。
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🥩</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        完整材料清單
                      </h4>
                      <p className="text-sm text-gray-600">
                        詳細的食材份量和替代選項
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">👨‍🍳</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        步驟式教學
                      </h4>
                      <p className="text-sm text-gray-600">
                        清楚的烹飪步驟和技巧說明
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">💡</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        烹飪小撇步
                      </h4>
                      <p className="text-sm text-gray-600">
                        專業廚師的實用建議和健康替代方案
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/recipes"
                    className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 font-medium text-lg inline-flex items-center gap-2 transition-all hover:scale-105"
                  >
                    🍳 查看食譜
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                      🔥 熱門料理
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      🌏 中英雙語
                    </span>
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      ⏱️ 簡單快速
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Math Zoo Game Section */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left side - Emoji icons */}
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 md:w-1/3 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-7xl mb-4 animate-bounce">🦁</div>
                  <div className="flex justify-center gap-2 text-4xl">
                    <span>🐰</span>
                    <span>🐻</span>
                    <span>🐸</span>
                  </div>
                  <div className="flex justify-center gap-2 text-3xl mt-2">
                    <span>🐱</span>
                    <span>🐶</span>
                    <span>🐼</span>
                  </div>
                </div>
              </div>

              {/* Right side - Content */}
              <div className="p-8 md:w-2/3">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  🦁 數學動物園
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  專為小朋友設計的趣味數學學習遊戲！透過可愛的動物角色，讓孩子在遊戲中愛上數學。
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🧮</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        數字分解遊戲
                      </h4>
                      <p className="text-sm text-gray-600">
                        學習20以內的數字加減法分解
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📊</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        算柱十進位遊戲
                      </h4>
                      <p className="text-sm text-gray-600">
                        用算柱理解十進位的概念
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🔢</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        倍數規律遊戲
                      </h4>
                      <p className="text-sm text-gray-600">
                        發現2、5、10倍數的規律
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/math-zoo"
                    className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-medium text-lg inline-flex items-center gap-2 transition-all hover:scale-105"
                  >
                    🎮 開始遊戲
                  </Link>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                      ⭐ 收集星星
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      🏆 排行榜
                    </span>
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      🎯 升級挑戰
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 to-indigo-100 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            {t("hero.title")}
          </h2>
          <p
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: t("hero.description"),
            }}
          />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/SearchTutor"
              className="bg-orange-600 text-white px-8 py-3 rounded-lg hover:bg-orange-700 font-medium text-lg"
            >
              {t("hero.findTutor")}
            </Link>
            <Link
              to="/SearchStudent"
              className="bg-orange-600 text-white px-8 py-3 rounded-lg hover:bg-orange-700 font-medium text-lg"
            >
              {t("hero.findStudent")}
            </Link>
            <Link
              to="/tutorCenters"
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 font-medium text-lg"
            >
              Tutor Centers 補習中心
            </Link>
            <Link
              to="/ads"
              className="border border-orange-600 text-orange-600 px-8 py-3 rounded-lg hover:bg-orange-50 font-medium text-lg"
            >
              {t("hero.freeRegisterAd")}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t("cta.title")}
          </h2>
          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            {t("cta.description")}
          </p>
          <Link
            to="/register"
            className="bg-white text-orange-600 px-8 py-3 rounded-lg hover:bg-gray-100 font-bold text-lg"
          >
            {t("cta.register")}
          </Link>
        </div>
      </section>
      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              {t("features.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t("features.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div
                key={feature.key}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              >
                <div className="text-4xl mb-6 text-center">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
                  {t(`features.items.${feature.key}.title`)}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {t(`features.items.${feature.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              {t("howItWorks.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t("howItWorks.subtitle")}
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start max-w-5xl mx-auto">
            {steps.map((step) => (
              <div key={step} className="flex-1 text-center px-6 mb-8 md:mb-0">
                <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {step}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  {t(`howItWorks.steps.step${step}.title`)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(`howItWorks.steps.step${step}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company */}
            <div>
              <h3 className="text-lg font-bold mb-4">TutorMesh 課聯</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    type="button"
                    className="text-gray-300 hover:text-white"
                  >
                    {t("footer.about")}
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="text-gray-300 hover:text-white"
                  >
                    {t("footer.contact")}
                  </button>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    type="button"
                    className="text-gray-300 hover:text-white"
                  >
                    {t("footer.privacy")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    type="button"
                    className="text-gray-300 hover:text-white"
                  >
                    {t("footer.terms")}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-bold mb-4">
                {t("footer.contactUs")}
              </h3>
              <ul className="space-y-2 text-gray-300">
                <li>info@tutormesh.com</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400">&copy; 2023 {t("footer.copyright")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
